import React, { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../state";
import {
  setPressureYMin,
  setPressureYMax,
  setTempYMin,
  setTempYMax,
  setXAxisMin,
  setXAxisMax,
} from "../../../../state/tpmsReducer";
import styles from "./style.module.scss";

interface TyreGroupAxisControlsProps {
  disabled?: boolean;
}

type XBound = "min" | "max";

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

function formatTime(timestamp?: number) {
  if (timestamp === undefined) return "";
  const date = new Date(timestamp);
  const hours24 = date.getHours();
  const meridiem = hours24 >= 12 ? "PM" : "AM";
  const hours12 = ((hours24 + 11) % 12) + 1;
  const hours = hours12.toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes} ${meridiem}`;
}

function parseTime(value: string) {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const match = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AaPp][Mm])?$/.exec(trimmed);
  if (!match) return null;
  const hours12 = Number(match[1]);
  const minutes = Number(match[2]);
  const inputMeridiem = match[3]?.toUpperCase();
  const currentMeridiem = new Date().getHours() >= 12 ? "PM" : "AM";
  const meridiem = inputMeridiem || currentMeridiem;

  let hours = hours12 % 12;
  if (meridiem === "PM") {
    hours += 12;
  }

  const today = new Date();
  today.setHours(hours, minutes, 0, 0);
  return today.getTime();
}

function shortcutTime(value: string) {
  const upper = value.toUpperCase();
  const digitsOnly = upper.replace(/\D/g, "").slice(0, 4);
  const lettersOnly = upper.replace(/[^APM]/g, "");
  const meridiem = lettersOnly.includes("PM")
    ? "PM"
    : lettersOnly.includes("AM")
    ? "AM"
    : "";

  if (digitsOnly.length <= 2) {
    return meridiem ? `${digitsOnly} ${meridiem}` : digitsOnly;
  }

  const paddedDigits = digitsOnly.padStart(4, "0");
  const hhmm = `${paddedDigits.slice(0, 2)}:${paddedDigits.slice(2)}`;
  return meridiem ? `${hhmm} ${meridiem}` : hhmm;
}

export default function TyreGroupAxisControls(
  props: TyreGroupAxisControlsProps,
) {
  const dispatch = useDispatch();
  const pressureYMin = useSelector(
    (state: RootState) => state.tpms.pressureYMin,
  );
  const pressureYMax = useSelector(
    (state: RootState) => state.tpms.pressureYMax,
  );
  const tempYMin = useSelector((state: RootState) => state.tpms.tempYMin);
  const tempYMax = useSelector((state: RootState) => state.tpms.tempYMax);
  const xAxisMin = useSelector((state: RootState) => state.tpms.xAxisMin);
  const xAxisMax = useSelector((state: RootState) => state.tpms.xAxisMax);

  const disabled = props.disabled || false;

  const handleYLimitChange = (
    event: ChangeEvent<HTMLInputElement>,
    actionCreator:
      | typeof setPressureYMin
      | typeof setPressureYMax
      | typeof setTempYMin
      | typeof setTempYMax,
  ) => {
    const value =
      event.target.value === "" ? undefined : Number(event.target.value);
    dispatch(actionCreator(value));
  };

  const [xText, setXText] = useState<Record<XBound, string>>({
    min: formatTime(xAxisMin),
    max: formatTime(xAxisMax),
  });

  useEffect(() => {
    setXText({
      min: formatTime(xAxisMin),
      max: formatTime(xAxisMax),
    });
  }, [xAxisMin, xAxisMax]);

  const setBoundText = (bound: XBound, value: string) => {
    setXText((previous) => ({ ...previous, [bound]: value }));
  };

  const handleXLimitChange = (
    bound: XBound,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setBoundText(bound, shortcutTime(event.target.value));
  };

  const commitXLimit = (bound: XBound) => {
    const textValue = xText[bound];
    const currentTimestamp = bound === "min" ? xAxisMin : xAxisMax;
    const otherTimestamp = bound === "min" ? xAxisMax : xAxisMin;
    const actionCreator = bound === "min" ? setXAxisMin : setXAxisMax;

    const parsed = parseTime(textValue);
    if (parsed === null) {
      setBoundText(bound, formatTime(currentTimestamp));
      return;
    }

    if (parsed === undefined) {
      dispatch(actionCreator(undefined));
      setBoundText(bound, "");
      return;
    }

    const nowTimestamp = Date.now();
    const minAllowedTimestamp = nowTimestamp - THREE_HOURS_MS;
    const clampedTimestamp = Math.min(
      nowTimestamp,
      Math.max(parsed, minAllowedTimestamp),
    );

    const isInvalidMinMaxPair =
      (bound === "min" &&
        otherTimestamp !== undefined &&
        clampedTimestamp > otherTimestamp) ||
      (bound === "max" &&
        otherTimestamp !== undefined &&
        clampedTimestamp < otherTimestamp);

    if (isInvalidMinMaxPair) {
      setBoundText(bound, formatTime(currentTimestamp));
      return;
    }

    dispatch(actionCreator(clampedTimestamp));
    setBoundText(bound, formatTime(clampedTimestamp));
  };

  const xFields: Array<{ key: XBound; label: string }> = [
    { key: "min", label: "XAxisMin" },
    { key: "max", label: "XAxisMax" },
  ];

  return (
    <div
      className={`${styles.axisControlContainer}${
        disabled ? ` ${styles.disabled}` : ""
      }`}
    >
      <div className={styles.axisControl}>
        <label>
          PAxisMin:
          <input
            type="number"
            value={pressureYMin !== undefined ? pressureYMin : ""}
            onChange={(e) => handleYLimitChange(e, setPressureYMin)}
            step={0.05}
            className={styles.yLimitInput}
          />
        </label>
        <label>
          PAxisMax:
          <input
            type="number"
            value={pressureYMax !== undefined ? pressureYMax : ""}
            onChange={(e) => handleYLimitChange(e, setPressureYMax)}
            step={0.05}
            className={styles.yLimitInput}
          />
        </label>
        <label>
          TAxisMin:
          <input
            type="number"
            value={tempYMin !== undefined ? tempYMin : ""}
            onChange={(e) => handleYLimitChange(e, setTempYMin)}
            step={1}
            className={styles.yLimitInput}
          />
        </label>
        <label>
          TAxisMax:
          <input
            type="number"
            value={tempYMax !== undefined ? tempYMax : ""}
            onChange={(e) => handleYLimitChange(e, setTempYMax)}
            step={1}
            className={styles.yLimitInput}
          />
        </label>
        {xFields.map((field) => (
          <label key={field.key}>
            {field.label}:
            <input
              type="text"
              placeholder="hh:mm AM/PM"
              inputMode="text"
              pattern="^(0?[1-9]|1[0-2]):[0-5]\\d(\\s?[AaPp][Mm])?$"
              value={xText[field.key]}
              onChange={(e) => handleXLimitChange(field.key, e)}
              onBlur={() => commitXLimit(field.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitXLimit(field.key);
                }
              }}
              className={styles.yLimitInput}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
